import React, { useContext, useMemo } from "react";
import { PlanTemplateCard } from "./PlanTemplateCard";
import {
  UNLIMITED_PLAN_TEMPLATE,
  LIMITED_CLASS_PLAN_TEMPLATE,
  PUNCH_CARD_PLAN_TEMPLATE,
  DROP_IN_PLAN_TEMPLATE,
  FREE_TRIAL_PLAN_TEMPLATE,
  PlanTemplate,
  COUNTRIES_WITH_PRICE_RECS,
} from "./plan-templates";
import { usePlans } from "../PlansProvider/PlansProvider";
import { PlanType } from "../../../apis/subscriptionApi";
import { usePlanPriceRecommendationsQuery } from "../../../apis/plansApi.hooks";
import { usePlanCategoriesQuery } from "../../../apis/subscriptionApi.hooks";
import { AppContext } from "../../../context/app";
import PpIcon from "../../../assets/img/pp-icon.svg";

interface PlanTemplateContainerProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const PlanTemplateContainer: React.FC<PlanTemplateContainerProps> = (
  props,
) => {
  const { onClose, onSuccess } = props;
  const { openPlanDrawer } = usePlans();
  const { client } = useContext(AppContext);

  const shouldFetchPriceRecommendations = useMemo(() => {
    return COUNTRIES_WITH_PRICE_RECS.includes(client?.countryName);
  }, [client?.countryName]);

  const {
    data: priceRecommendations,
    isLoading: isLoadingPriceRecommendations,
  } = usePlanPriceRecommendationsQuery(
    undefined,
    shouldFetchPriceRecommendations,
  );

  const { data: planCategories } = usePlanCategoriesQuery();

  const defaultPlanCategory = planCategories?.data.find(
    (category) => category.name === "General",
  );

  const enhancedPlanTemplates = useMemo(() => {
    return [
      UNLIMITED_PLAN_TEMPLATE,
      LIMITED_CLASS_PLAN_TEMPLATE,
      PUNCH_CARD_PLAN_TEMPLATE,
      DROP_IN_PLAN_TEMPLATE,
      FREE_TRIAL_PLAN_TEMPLATE,
    ].map((template) => {
      const enhancedTemplate = {
        ...template,
        planFormValues: { ...template.planFormValues },
      };

      if (template.id === "free-trial") {
        enhancedTemplate.recommendedPrice = 0;
        enhancedTemplate.planFormValues.price = 0;
      } else {
        const priceRecommendation = priceRecommendations?.find(
          (recommendation) => recommendation.planType === template.apiName,
        );

        let recommendedPrice = priceRecommendation?.medianAmount;

        // limited-class and punch-card plans are priced per session
        if (
          recommendedPrice &&
          (template.id === "limited-class" || template.id === "punch-card")
        ) {
          const sessionsCount = template.planFormValues.sessionsCount ?? 1;
          recommendedPrice = recommendedPrice * sessionsCount;
        }
        if (recommendedPrice) {
          recommendedPrice = Math.round(recommendedPrice);
        }

        enhancedTemplate.recommendedPrice = recommendedPrice;
        enhancedTemplate.planFormValues.price = recommendedPrice;
      }

      enhancedTemplate.planFormValues.planCategory = defaultPlanCategory;

      return enhancedTemplate;
    });
  }, [priceRecommendations, defaultPlanCategory]);

  const handlePlanTypeClick = (
    planType: PlanType,
    planTemplate: PlanTemplate,
  ) => {
    openPlanDrawer({ planType, onSuccess, planTemplate });
    onClose();
  };

  return (
    <div className="tw-w-full tw-mx-auto">
      <div className="tw-mb-4 tw-w-full tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between">
        <p className="tw-text-xl tw-py-2">Recommended plans</p>
        <div className="tw-flex tw-items-center">
          <PpIcon className="tw-w-10 tw-h-10" />
          <p className="tw-text-sm tw-text-gray-500">
            Pricing insights from PushPress gyms in your region.
          </p>
        </div>
      </div>

      <div className="tw-space-y-4">
        {enhancedPlanTemplates.map((template) => (
          <PlanTemplateCard
            key={template.id}
            template={template}
            onClick={() => handlePlanTypeClick(template.planType, template)}
            isLoading={isLoadingPriceRecommendations}
            shouldFetchPriceRecommendations={shouldFetchPriceRecommendations}
          />
        ))}
      </div>
    </div>
  );
};
